/**
 * Unified Visualization Dashboard
 * Combines DHIS2-inspired widgets with Advanced Analysis features from SIS Integration
 * Tabs: Overview | Advanced Charts | Maps | Reports | Excel Export
 */
import React, { useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Select, MenuItem, FormControl,
  InputLabel, Chip, IconButton, Tooltip, Breadcrumbs, Link, Button,
  Divider, Tab, Tabs, Paper, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, ButtonGroup,
} from '@mui/material';
import {
  FilterList, Refresh, Download, Fullscreen,
  BarChart as BarChartIcon, ShowChart, PieChart as PieChartIcon,
  TableChart, Speed, TrendingUp, People, School, Person,
  Map as MapIcon, Assessment as ReportsIcon, TableView as ExcelIcon,
  LocationOn, AccountBalance as RegionIcon,
  Groups as GroupsIcon, Warning as WarningIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend,
  AreaChart, Area, ComposedChart,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import SingleValueCard from '../../components/Dashboard/SingleValueCard';
import GaugeChart from '../../components/Dashboard/GaugeChart';
import { CHART_COLORS, formatNumber, INDICATOR_CATEGORIES } from '../../theme/dashboardTheme';
import { getAnalysisData, getAvailableYears, getChartData, getRegions, getDistricts } from '../../services/analysisApi';
import TeacherDeploymentDashboard from '../TeacherDeployment/TeacherDeploymentDashboard';
import EarlyWarningDashboard from '../EarlyWarning/EarlyWarningDashboard';
import { ZanzibarMap } from '../../components/Map';

// ========== Constants ==========
const ANALYSIS_TYPES = [
  { value: 'enrollment', label: 'Student Enrollment' },
  { value: 'infrastructure', label: 'School Infrastructure' },
  { value: 'performance', label: 'Academic Performance' },
  { value: 'staff', label: 'Staff Distribution' },
  { value: 'gender_parity', label: 'Gender Parity Index' },
];

const CHART_STYLES = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'line', label: 'Line Chart' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'area', label: 'Area Chart' },
];

const SPLIT_BY_OPTIONS = [
  { value: 'none', label: '(none)' },
  { value: 'region', label: 'By Region' },
  { value: 'district', label: 'By District' },
  { value: 'school_level', label: 'By School Level' },
  { value: 'gender', label: 'By Gender' },
];

const SORT_OPTIONS = [
  { value: 'none', label: '(no sort)' },
  { value: 'asc', label: 'Ascending' },
  { value: 'desc', label: 'Descending' },
  { value: 'alphabetical', label: 'Alphabetical' },
];

const ANALYSIS_LEVELS = [
  { value: 'region', label: 'Regional View', icon: <RegionIcon fontSize="small" /> },
  { value: 'district', label: 'District View', icon: <LocationOn fontSize="small" /> },
  { value: 'school', label: 'School View', icon: <School fontSize="small" /> },
];

// ========== Demo data for Overview tab ==========
const enrollmentTrends = [
  { year: '2020', prePrimary: 42500, primary: 245000, secondary: 68000 },
  { year: '2021', prePrimary: 45800, primary: 252000, secondary: 72500 },
  { year: '2022', prePrimary: 48200, primary: 261000, secondary: 77800 },
  { year: '2023', prePrimary: 51500, primary: 275000, secondary: 82100 },
  { year: '2024', prePrimary: 55200, primary: 289000, secondary: 86400 },
  { year: '2025', prePrimary: 58900, primary: 298947, secondary: 89056 },
  { year: '2026', prePrimary: 62100, primary: 310500, secondary: 93200 },
];

const districtEnrollment = [
  { name: 'Mjini Magharibi', total: 68542, male: 33180, female: 35362 },
  { name: 'Kaskazini A', total: 45230, male: 22150, female: 23080 },
  { name: 'Kaskazini B', total: 38720, male: 19200, female: 19520 },
  { name: 'Kusini', total: 42180, male: 20500, female: 21680 },
  { name: 'Kati', total: 31450, male: 15800, female: 15650 },
  { name: 'Magharibi A', total: 36890, male: 18100, female: 18790 },
  { name: 'Magharibi B', total: 28350, male: 14200, female: 14150 },
  { name: 'Wete', total: 32100, male: 15900, female: 16200 },
  { name: 'Chake Chake', total: 27840, male: 13600, female: 14240 },
  { name: 'Mkoani', total: 18400, male: 9100, female: 9300 },
];

const schoolTypeDistribution = [
  { name: 'Government', value: 1285, percentage: 83.6 },
  { name: 'Private', value: 178, percentage: 11.6 },
  { name: 'Community', value: 52, percentage: 3.4 },
  { name: 'Religious', value: 22, percentage: 1.4 },
];

const infrastructureData = [
  { facility: 'Clean Water', percentage: 72 },
  { facility: 'Electricity', percentage: 65 },
  { facility: 'Library', percentage: 34 },
  { facility: 'Computer Lab', percentage: 18 },
  { facility: 'Science Lab', percentage: 28 },
  { facility: 'Toilets (Adequate)', percentage: 58 },
  { facility: 'Sports Field', percentage: 45 },
  { facility: 'Fence/Wall', percentage: 41 },
];

const monthlyAttendance = [
  { month: 'Jan', teachers: 92, students: 88 },
  { month: 'Feb', teachers: 94, students: 91 },
  { month: 'Mar', teachers: 91, students: 87 },
  { month: 'Apr', teachers: 89, students: 84 },
  { month: 'May', teachers: 93, students: 90 },
  { month: 'Jun', teachers: 88, students: 82 },
  { month: 'Jul', teachers: 45, students: 12 },
  { month: 'Aug', teachers: 42, students: 10 },
  { month: 'Sep', teachers: 95, students: 92 },
  { month: 'Oct', teachers: 93, students: 89 },
  { month: 'Nov', teachers: 91, students: 87 },
  { month: 'Dec', teachers: 86, students: 78 },
];

// ========== TabPanel ==========
interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>
);

// ========== Main Component ==========
const VisualizationDashboard: React.FC = () => {
  const [mainTab, setMainTab] = useState(0);
  const [selectedYear, setSelectedYear] = useState('2026');
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  const [filterChips, setFilterChips] = useState<string[]>([]);

  // Advanced Charts state
  const [chartConfig, setChartConfig] = useState({
    indicator: 'enrollment',
    xAxis: 'regions',
    splitBy: 'none',
    year: '2026',
    chartStyle: 'bar',
    sort: 'none',
  });

  // Maps state
  const [selectedLevel, setSelectedLevel] = useState('district');
  const [selectedType, setSelectedType] = useState('enrollment');
  const [mapView, setMapView] = useState('standard');

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportingType, setExportingType] = useState('');
  const [exportedFile, setExportedFile] = useState<{ blob: Blob; filename: string } | null>(null);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedSchoolType, setSelectedSchoolType] = useState('');

  // ===== API Queries =====
  const { data: availableYears } = useQuery({
    queryKey: ['available-years'],
    queryFn: getAvailableYears,
    enabled: mainTab >= 1,
  });

  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ['chart-data', chartConfig.indicator, chartConfig.xAxis, chartConfig.splitBy, chartConfig.year],
    queryFn: () => getChartData({ indicator: chartConfig.indicator, xAxis: chartConfig.xAxis, splitBy: chartConfig.splitBy, year: chartConfig.year }),
    enabled: mainTab === 1,
  });

  const { data: geoData, isLoading: geoLoading, error: geoError } = useQuery({
    queryKey: ['geographic-analysis', selectedLevel, selectedType, selectedYear],
    queryFn: () => getAnalysisData('geographic', { level: selectedLevel, type: selectedType, academic_year: selectedYear }),
    enabled: mainTab === 2,
  });

  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: ['trend-analysis', selectedType],
    queryFn: () => getAnalysisData('trends', { dimension: selectedType, period: '5years' }),
    enabled: mainTab === 3,
  });

  const { data: kpiData, isLoading: kpiLoading } = useQuery({
    queryKey: ['performance-indicators'],
    queryFn: () => getAnalysisData('kpis', { scope: 'national' }),
    enabled: mainTab === 3,
  });

  const { data: regions } = useQuery({
    queryKey: ['regions'],
    queryFn: getRegions,
    enabled: mainTab === 4,
  });

  const { data: districts } = useQuery({
    queryKey: ['districts', selectedRegion],
    queryFn: () => getDistricts(selectedRegion || undefined),
    enabled: mainTab === 4 && !!selectedRegion,
  });

  // ===== Export handler =====
  const handleExportExcel = async (reportType: string) => {
    if (isExporting) return;
    setIsExporting(true);
    setExportingType(reportType);
    setExportedFile(null);
    try {
      const params = new URLSearchParams();
      if (chartConfig.year) params.append('year', chartConfig.year);
      params.append('report_type', reportType);
      if (selectedRegion) params.append('region_id', selectedRegion);
      if (selectedDistrict && selectedDistrict !== 'all') params.append('district_id', selectedDistrict);
      if (selectedSchoolType) params.append('school_type', selectedSchoolType);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${baseUrl}/sis-integration/export-schools-excel/?${params.toString()}`, { method: 'GET', signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`Export failed: ${response.status}`);
      const blob = await response.blob();
      const filename = `Zanzibar_${reportType.charAt(0).toUpperCase() + reportType.slice(1)}_${chartConfig.year}.xlsx`;
      setExportedFile({ blob, filename });
    } catch (error: any) {
      alert(`Export failed: ${error.message}`);
    } finally {
      setIsExporting(false);
      setExportingType('');
    }
  };

  const handleDownloadFile = () => {
    if (!exportedFile) return;
    const url = window.URL.createObjectURL(exportedFile.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportedFile.filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    setExportedFile(null);
  };

  const addFilter = (filter: string) => { if (!filterChips.includes(filter)) setFilterChips([...filterChips, filter]); };
  const removeFilter = (filter: string) => { setFilterChips(filterChips.filter(f => f !== filter)); };

  // ========== RENDER: Overview Tab ==========
  const renderOverview = () => (
    <Box>
      {/* KPI Single Value Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}>
          <SingleValueCard title="Total Schools" value={1537} previousValue={1498} icon={<School />} colorScheme="primary" subtitle="Active schools" size="small" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <SingleValueCard title="Total Students" value={369702} previousValue={345200} format="integer" icon={<People />} colorScheme="success" subtitle="All levels" size="small" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <SingleValueCard title="Total Teachers" value={3211} previousValue={3050} icon={<Person />} colorScheme="info" subtitle="Active staff" size="small" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <SingleValueCard title="S/T Ratio" value={115.1} previousValue={113.2} format="ratio" colorScheme="warning" target={40} subtitle="Target: 40:1" size="small" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <SingleValueCard title="Completion Rate" value={78.5} previousValue={75.2} format="percentage" colorScheme="purple" target={90} subtitle="Primary level" size="small" />
        </Grid>
      </Grid>

      {/* Main Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Enrollment Trends</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Student enrollment by education level (2020-2025)</Typography>
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={enrollmentTrends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF2" />
                  <XAxis dataKey="year" fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(v) => formatNumber(v)} />
                  <RechartsTooltip formatter={(value: number) => value.toLocaleString()} />
                  <Legend />
                  <Area type="monotone" dataKey="prePrimary" name="Pre-Primary" stackId="1" stroke={CHART_COLORS[0]} fill={CHART_COLORS[0]} fillOpacity={0.6} />
                  <Area type="monotone" dataKey="primary" name="Primary" stackId="1" stroke={CHART_COLORS[1]} fill={CHART_COLORS[1]} fillOpacity={0.6} />
                  <Area type="monotone" dataKey="secondary" name="Secondary" stackId="1" stroke={CHART_COLORS[2]} fill={CHART_COLORS[2]} fillOpacity={0.6} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>School Type Distribution</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={schoolTypeDistribution} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, percentage }) => `${name} ${percentage}%`} labelLine>
                    {schoolTypeDistribution.map((_, index) => (<Cell key={index} fill={CHART_COLORS[index]} />))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ mt: 1 }}>
                {schoolTypeDistribution.map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: CHART_COLORS[i] }} />
                      <Typography variant="caption">{item.name}</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{item.value.toLocaleString()}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* District & Gauges */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>District Enrollment Comparison (by Gender)</Typography>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={districtEnrollment} margin={{ bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF2" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" fontSize={11} />
                  <YAxis fontSize={11} tickFormatter={(v) => formatNumber(v)} />
                  <RechartsTooltip formatter={(value: number) => value.toLocaleString()} />
                  <Legend />
                  <Bar dataKey="male" name="Male" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="female" name="Female" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <GaugeChart title="Gender Parity Index" value={0.98} min={0} max={2} format="decimal" thresholds={[
                { value: 45, color: '#D32F2F', label: 'Disparity' }, { value: 48, color: '#FFC324', label: 'Near' },
                { value: 52, color: '#388E3C', label: 'Parity' }, { value: 55, color: '#FFC324', label: 'Near' }, { value: 100, color: '#D32F2F', label: 'Disparity' },
              ]} subtitle="Target: 1.00" />
            </Grid>
            <Grid item xs={12}>
              <GaugeChart title="Data Completeness" value={87} format="percentage" subtitle="87% of required fields populated" />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Infrastructure & Attendance */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Infrastructure Access Rate</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={infrastructureData} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF2" />
                  <XAxis type="number" domain={[0, 100]} fontSize={11} tickFormatter={(v) => `${v}%`} />
                  <YAxis type="category" dataKey="facility" fontSize={11} width={95} />
                  <RechartsTooltip formatter={(value: number) => `${value}%`} />
                  <Bar dataKey="percentage" radius={[0, 4, 4, 0]}>
                    {infrastructureData.map((entry, index) => (<Cell key={index} fill={entry.percentage >= 70 ? '#388E3C' : entry.percentage >= 50 ? '#FFC324' : '#D32F2F'} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Monthly Attendance Rate</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={monthlyAttendance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF2" />
                  <XAxis dataKey="month" fontSize={12} />
                  <YAxis domain={[0, 100]} fontSize={12} tickFormatter={(v) => `${v}%`} />
                  <RechartsTooltip formatter={(value: number) => `${value}%`} />
                  <Legend />
                  <Line type="monotone" dataKey="teachers" name="Teachers" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="students" name="Students" stroke={CHART_COLORS[2]} strokeWidth={2} dot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // ========== RENDER: Advanced Charts Tab ==========
  const renderAdvancedCharts = () => (
    <Box>
      {/* Chart Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Chart Configuration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Configure your chart view. Select dimensions, filters, and styles.</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Indicator</InputLabel>
                <Select value={chartConfig.indicator} label="Indicator" onChange={(e) => setChartConfig(prev => ({ ...prev, indicator: e.target.value }))}>
                  {ANALYSIS_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>X-Axis</InputLabel>
                <Select value={chartConfig.xAxis} label="X-Axis" onChange={(e) => setChartConfig(prev => ({ ...prev, xAxis: e.target.value }))}>
                  <MenuItem value="all">All Nations</MenuItem>
                  <MenuItem value="regions">By Regions</MenuItem>
                  <MenuItem value="districts">By Districts</MenuItem>
                  <MenuItem value="schools">By Schools</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Split by</InputLabel>
                <Select value={chartConfig.splitBy} label="Split by" onChange={(e) => setChartConfig(prev => ({ ...prev, splitBy: e.target.value }))}>
                  {SPLIT_BY_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select value={chartConfig.year} label="Year" onChange={(e) => setChartConfig(prev => ({ ...prev, year: e.target.value }))}>
                  {(availableYears || [2026, 2025, 2024, 2023, 2022]).map((y: number) => <MenuItem key={y} value={y.toString()}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Chart Style</InputLabel>
                <Select value={chartConfig.chartStyle} label="Chart Style" onChange={(e) => setChartConfig(prev => ({ ...prev, chartStyle: e.target.value }))}>
                  {CHART_STYLES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort</InputLabel>
                <Select value={chartConfig.sort} label="Sort" onChange={(e) => setChartConfig(prev => ({ ...prev, sort: e.target.value }))}>
                  {SORT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" startIcon={<BarChartIcon />} sx={{ backgroundColor: '#333', '&:hover': { backgroundColor: '#555' }, textTransform: 'none' }}>
                View {chartConfig.chartStyle.charAt(0).toUpperCase() + chartConfig.chartStyle.slice(1)} Chart
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Chart Results */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {ANALYSIS_TYPES.find(t => t.value === chartConfig.indicator)?.label} - {chartConfig.chartStyle.charAt(0).toUpperCase() + chartConfig.chartStyle.slice(1)} Chart
            </Typography>
            <Button variant="contained" size="small" startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <ExcelIcon />} onClick={() => handleExportExcel('enrollment')} disabled={isExporting}>
              Export Data
            </Button>
          </Box>

          {chartLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : chartData?.data?.length > 0 ? (
            <>
              <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {chartConfig.indicator} by {chartConfig.xAxis} &bull; Year: {chartConfig.year} &bull; Data points: {chartData.data.length}
                </Typography>
              </Paper>

              {/* Bar Chart */}
              {chartConfig.chartStyle === 'bar' && (
                <Box sx={{ mt: 2 }}>
                  {chartData.data.slice(0, 15).map((item: any, index: number) => {
                    const maxValue = Math.max(...chartData.data.map((d: any) => d.value || 0));
                    const percentage = ((item.value || 0) / maxValue) * 100;
                    return (
                      <Box key={index} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.label}</Typography>
                          <Typography variant="body2" color="text.secondary">{(item.value || 0).toLocaleString()}</Typography>
                        </Box>
                        <Box sx={{ width: '100%', height: 22, backgroundColor: '#f0f0f0', borderRadius: 1, overflow: 'hidden' }}>
                          <Box sx={{ width: `${percentage}%`, height: '100%', backgroundColor: CHART_COLORS[index % CHART_COLORS.length], transition: 'width 0.5s ease' }} />
                        </Box>
                        {chartConfig.splitBy === 'gender' && (
                          <Box sx={{ display: 'flex', gap: 2, mt: 0.3 }}>
                            <Typography variant="caption" color="primary">Male: {(item.male || 0).toLocaleString()}</Typography>
                            <Typography variant="caption" color="secondary">Female: {(item.female || 0).toLocaleString()}</Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Pie Chart */}
              {chartConfig.chartStyle === 'pie' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {chartData.data.slice(0, 8).map((item: any, index: number) => {
                    const total = chartData.data.reduce((sum: number, d: any) => sum + (d.value || 0), 0);
                    const pct = ((item.value || 0) / total * 100).toFixed(1);
                    return (
                      <Grid item xs={6} sm={3} key={index}>
                        <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: CHART_COLORS[index % CHART_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, mx: 'auto', mb: 1, fontSize: '0.85rem' }}>{pct}%</Box>
                          <Typography variant="body2" noWrap>{item.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{(item.value || 0).toLocaleString()}</Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              {/* Data Table */}
              <TableContainer component={Paper} sx={{ maxHeight: 350, mt: 2 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{chartConfig.xAxis === 'regions' ? 'Region' : 'District'}</strong></TableCell>
                      <TableCell align="right"><strong>Total</strong></TableCell>
                      {chartConfig.splitBy === 'gender' && (<><TableCell align="right"><strong>Male</strong></TableCell><TableCell align="right"><strong>Female</strong></TableCell><TableCell align="right"><strong>% Female</strong></TableCell></>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chartData.data.map((item: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{item.label}</TableCell>
                        <TableCell align="right"><strong>{(item.value || 0).toLocaleString()}</strong></TableCell>
                        {chartConfig.splitBy === 'gender' && (
                          <><TableCell align="right">{(item.male || 0).toLocaleString()}</TableCell><TableCell align="right">{(item.female || 0).toLocaleString()}</TableCell>
                          <TableCell align="right"><Chip label={`${Math.round((item.female / (item.value || 1)) * 100)}%`} size="small" color={Math.round((item.female / (item.value || 1)) * 100) > 45 ? 'success' : 'warning'} /></TableCell></>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="info">No chart data available. Configure the chart and click "View Chart" to load data from the SIS database.</Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  // ========== RENDER: Maps Tab ==========
  const renderMaps = () => (
    <Card sx={{ height: 'calc(100vh - 200px)', minHeight: 550 }}>
      <CardContent sx={{ p: '0 !important', height: '100%' }}>
        <ZanzibarMap height="100%" />
      </CardContent>
    </Card>
  );

  // ========== RENDER: Reports Tab ==========
  const renderReports = () => {
    const trends = trendData?.data?.trends || [];
    const kpis = kpiData?.data || {};
    return (
      <Box>
        {/* KPI Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Schools', value: kpis.total_schools, icon: <School color="primary" /> },
            { label: 'Total Students', value: kpis.total_students, icon: <People color="info" /> },
            { label: 'S/T Ratio', value: kpis.student_teacher_ratio ? `${kpis.student_teacher_ratio}:1` : '—', icon: <Speed color="success" /> },
            { label: 'Female %', value: kpis.female_enrollment_pct ? `${kpis.female_enrollment_pct}%` : '—', icon: <People color="secondary" /> },
          ].map((kpi, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                {kpi.icon}
                <Typography variant="h5" sx={{ my: 1 }}>{typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
              </CardContent></Card>
            </Grid>
          ))}
        </Grid>

        {/* Trend Table */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Enrollment Trends</Typography>
              <Button variant="outlined" size="small" startIcon={<ExcelIcon />} onClick={() => handleExportExcel('enrollment')} disabled={isExporting}>Export Trend Data</Button>
            </Box>
            {trendLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : trends.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell><strong>Academic Year</strong></TableCell>
                    <TableCell align="right"><strong>Total Students</strong></TableCell>
                    <TableCell align="right"><strong>Male</strong></TableCell>
                    <TableCell align="right"><strong>Female</strong></TableCell>
                    <TableCell align="right"><strong>Growth Rate</strong></TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {trends.map((t: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{t.academic_year}</TableCell>
                        <TableCell align="right">{(t.total_students || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">{(t.male_students || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">{(t.female_students || 0).toLocaleString()}</TableCell>
                        <TableCell align="right"><Chip label={`${t.growth_rate || 0}%`} color={t.growth_rate > 0 ? 'success' : t.growth_rate < 0 ? 'error' : 'default'} size="small" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : <Alert severity="info">No trend data available. Ensure the backend is running and SIS database is connected.</Alert>}
          </CardContent>
        </Card>
      </Box>
    );
  };

  // ========== RENDER: Excel Export Tab ==========
  const renderExcelExport = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Excel Report Export</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Generate and download comprehensive Excel reports from the SIS database.</Typography>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select value={chartConfig.year} label="Year" onChange={(e) => setChartConfig(prev => ({ ...prev, year: e.target.value }))}>
                {[2026, 2025, 2024, 2023, 2022].map(y => <MenuItem key={y} value={y.toString()}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Region</InputLabel>
              <Select value={selectedRegion} label="Region" onChange={(e) => setSelectedRegion(e.target.value)}>
                <MenuItem value="">All Regions</MenuItem>
                {(regions || []).map((r: any) => <MenuItem key={r.id || r.region_id} value={r.id || r.region_id}>{r.name || r.region_name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>District</InputLabel>
              <Select value={selectedDistrict} label="District" onChange={(e) => setSelectedDistrict(e.target.value)} disabled={!selectedRegion}>
                <MenuItem value="all">All Districts</MenuItem>
                {(districts || []).map((d: any) => <MenuItem key={d.id || d.district_id} value={d.id || d.district_id}>{d.name || d.district_name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>School Type</InputLabel>
              <Select value={selectedSchoolType} label="School Type" onChange={(e) => setSelectedSchoolType(e.target.value)}>
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="government">Government</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Export Buttons */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { type: 'schools', label: 'Export Schools', desc: 'Complete school data with facilities', icon: <School /> },
            { type: 'enrollment', label: 'Enrollment Report', desc: 'Student enrollment by demographics', icon: <People /> },
            { type: 'infrastructure', label: 'Infrastructure Report', desc: 'Facilities and capacity data', icon: <RegionIcon /> },
          ].map(report => (
            <Grid item xs={12} sm={4} key={report.type}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                {report.icon}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>{report.label}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>{report.desc}</Typography>
                <Button variant="contained" size="small" startIcon={isExporting && exportingType === report.type ? <CircularProgress size={16} color="inherit" /> : <Download />}
                  onClick={() => handleExportExcel(report.type)} disabled={isExporting} fullWidth>
                  {isExporting && exportingType === report.type ? 'Generating...' : 'Download'}
                </Button>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Download notification */}
        {exportedFile && (
          <Alert severity="success" action={<Button color="inherit" size="small" onClick={handleDownloadFile}>Download Now</Button>}>
            Report is ready! File: {exportedFile.filename}
          </Alert>
        )}

        {isExporting && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Generating report... This may take up to 2 minutes for large datasets.</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // ========== MAIN RENDER ==========
  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" href="/">Home</Link>
          <Typography color="text.primary">Visualization Dashboard</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Education Analytics Dashboard</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Comprehensive view of education indicators across Zanzibar</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<Refresh />}>Refresh</Button>
            <Button size="small" variant="outlined" startIcon={<Download />}>Export</Button>
          </Box>
        </Box>
      </Box>

      {/* Global Filters */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', backgroundColor: '#F8F9FA' }}>
        <FilterList sx={{ color: 'text.secondary' }} />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select value={selectedYear} label="Year" onChange={(e) => setSelectedYear(e.target.value)}>
            {['2026', '2025', '2024', '2023', '2022', '2021', '2020'].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>District</InputLabel>
          <Select value={selectedDistrict} label="District" onChange={(e) => setSelectedDistrict(e.target.value)}>
            <MenuItem value="all">All Districts</MenuItem>
            {districtEnrollment.map(d => <MenuItem key={d.name} value={d.name}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        {filterChips.map(f => (
          <Chip key={f} label={f} size="small" onDelete={() => removeFilter(f)} sx={{ backgroundColor: '#212934', color: '#fff' }} />
        ))}
        {filterChips.length > 0 && <Button size="small" onClick={() => setFilterChips([])}>Clear all</Button>}
      </Paper>

      {/* Main Tab Navigation */}
      <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 } }}>
        <Tab icon={<Speed />} label="Overview" iconPosition="start" />
        <Tab icon={<BarChartIcon />} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Advanced Charts<Chip label="SIS" size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#E8F5E9', color: '#2E7D32' }} /></Box>} iconPosition="start" />
        <Tab icon={<MapIcon />} label="Maps" iconPosition="start" />
        <Tab icon={<ReportsIcon />} label="Reports" iconPosition="start" />
        <Tab icon={<ExcelIcon />} label="Excel Export" iconPosition="start" />
        <Tab icon={<GroupsIcon />} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>PTR & Teachers<Chip label="SIS" size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#E8F5E9', color: '#2E7D32' }} /></Box>} iconPosition="start" />
        <Tab icon={<WarningIcon />} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Early Warning<Chip label="SIS" size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#FFF3E0', color: '#E65100' }} /></Box>} iconPosition="start" />
      </Tabs>

      {/* Tab Panels */}
      <TabPanel value={mainTab} index={0}>{renderOverview()}</TabPanel>
      <TabPanel value={mainTab} index={1}>{renderAdvancedCharts()}</TabPanel>
      <TabPanel value={mainTab} index={2}>{renderMaps()}</TabPanel>
      <TabPanel value={mainTab} index={3}>{renderReports()}</TabPanel>
      <TabPanel value={mainTab} index={4}>{renderExcelExport()}</TabPanel>
      <TabPanel value={mainTab} index={5}><TeacherDeploymentDashboard /></TabPanel>
      <TabPanel value={mainTab} index={6}><EarlyWarningDashboard /></TabPanel>
    </Box>
  );
};

export default VisualizationDashboard;
